import { forwardRef, type InputHTMLAttributes, type ReactNode, useId } from 'react';

type InputType = 'text' | 'email' | 'password' | 'tel' | 'number';

interface GlassInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  type?: InputType;
  label?: string;
  error?: string;
  hint?: string;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

export const GlassInput = forwardRef<HTMLInputElement, GlassInputProps>(
  function GlassInput(
    {
      type = 'text',
      label,
      error,
      hint,
      iconLeft,
      iconRight,
      className = '',
      id: externalId,
      disabled,
      ...rest
    },
    ref,
  ) {
    const autoId = useId();
    const id = externalId ?? autoId;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;

    const describedBy = [error && errorId, hint && !error && hintId]
      .filter(Boolean)
      .join(' ') || undefined;

    return (
      <div className={`flex flex-col gap-1.5 ${className}`}>
        {label && (
          <label
            htmlFor={id}
            className="text-sm font-medium text-[var(--text-secondary)]"
          >
            {label}
          </label>
        )}

        <div className="relative">
          {iconLeft && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none">
              {iconLeft}
            </span>
          )}

          <input
            ref={ref}
            id={id}
            type={type}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={[
              'w-full',
              // 16px font prevents iOS zoom
              'text-[16px] sm:text-sm',
              'min-h-[44px] px-3 py-2.5',
              'rounded-[var(--radius-md)]',
              'bg-[var(--glass-bg)] border backdrop-blur-[var(--glass-blur)]',
              'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
              'transition-colors duration-150',
              'focus:outline-none focus:ring-2 focus:ring-offset-1',
              error
                ? 'border-[var(--brand-danger)] focus:ring-[var(--brand-danger)]'
                : 'border-[var(--glass-border)] focus:ring-[var(--brand-primary)]',
              disabled && 'opacity-50 cursor-not-allowed',
              iconLeft && 'pl-10',
              iconRight && 'pr-10',
            ]
              .filter(Boolean)
              .join(' ')}
            {...rest}
          />

          {iconRight && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">
              {iconRight}
            </span>
          )}
        </div>

        {error && (
          <p id={errorId} className="text-xs text-[var(--brand-danger)]" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className="text-xs text-[var(--text-muted)]">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
